import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { TAG_TYPES, createTag } from "../../utilities/tags-service";

// User input goes into the filter regex below — escape it so characters
// like ( or [ can't throw during render
function escapeRegex(text) {
  return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// Approximate max height of the open menu (max-h-48 plus padding); used
// to decide whether to open upward
const MENU_MAX_HEIGHT = 200;

// Multi-select tag combobox. The existing tags stay tucked away in a
// dropdown (opened by clicking or typing in the search box) instead of
// rendering as an always-visible pill wall; typing filters the list, and
// unmatched text can be created as a new tag inline. Selected tags render
// as removable chips inline in the field, ahead of the search input.
//
// The menu is rendered through a portal with fixed positioning so the
// form card's overflow-y-auto can't clip it; it follows the input on
// scroll/resize and flips upward when there's no room below.
export default function TagSelect({
  type,
  values,
  onChange,
  options,
  onCreated,
}) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState(null);
  const containerRef = useRef(null);
  // The bordered field wrapper (chips + input) — the menu anchors to this
  const fieldRef = useRef(null);
  const inputRef = useRef(null);
  const menuRef = useRef(null);
  const { nameField, label } = TAG_TYPES[type];

  const updateMenuPos = useCallback(() => {
    const rect = fieldRef.current?.getBoundingClientRect();
    if (!rect) return;
    const openUp =
      window.innerHeight - rect.bottom < MENU_MAX_HEIGHT &&
      rect.top > MENU_MAX_HEIGHT;
    const next = {
      left: rect.left,
      width: rect.width,
      top: openUp ? undefined : rect.bottom + 4,
      bottom: openUp ? window.innerHeight - rect.top + 4 : undefined,
    };
    // Bail out of the state update when nothing moved so the rAF loop
    // below doesn't re-render every frame
    setMenuPos((prev) =>
      prev &&
      prev.left === next.left &&
      prev.width === next.width &&
      prev.top === next.top &&
      prev.bottom === next.bottom
        ? prev
        : next,
    );
  }, []);

  // Keep the fixed-position menu glued to the input while open. The form
  // card scrolls internally, so listen on every scrollable ancestor of
  // the input (element scrolls don't reliably reach a window-level
  // listener), plus the window itself for page scroll and resize.
  useEffect(() => {
    if (!open) return;
    updateMenuPos();
    const scrollables = [];
    let node = fieldRef.current?.parentElement;
    while (node) {
      const style = getComputedStyle(node);
      if (/auto|scroll/.test(style.overflowY + style.overflowX)) {
        scrollables.push(node);
      }
      node = node.parentElement;
    }
    window.addEventListener("scroll", updateMenuPos, true);
    window.addEventListener("resize", updateMenuPos);
    for (const el of scrollables) el.addEventListener("scroll", updateMenuPos);
    return () => {
      window.removeEventListener("scroll", updateMenuPos, true);
      window.removeEventListener("resize", updateMenuPos);
      for (const el of scrollables) {
        el.removeEventListener("scroll", updateMenuPos);
      }
    };
  }, [open, updateMenuPos]);

  // Close on outside clicks — the menu lives in a portal, so check both
  useEffect(() => {
    function handleClick(evt) {
      if (
        containerRef.current?.contains(evt.target) ||
        menuRef.current?.contains(evt.target)
      ) {
        return;
      }
      setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const rx = new RegExp(escapeRegex(search), "i");
  const filtered = options.filter((tag) => rx.test(tag[nameField]));
  const selected = options.filter((tag) => values.includes(tag._id));
  const exactMatch = options.find(
    (tag) => tag[nameField].toLowerCase() === search.trim().toLowerCase(),
  );
  const canAdd = search.trim() !== "" && !exactMatch;

  function toggle(id) {
    onChange(
      values.includes(id)
        ? values.filter((value) => value !== id)
        : [...values, id],
    );
  }

  async function handleAdd() {
    const created = await createTag(type, search);
    // The service returns the Error on failure (e.g. 409 duplicate)
    if (!created?._id) {
      console.log(created);
      return;
    }
    onCreated(created);
    onChange([...values, created._id]);
    setSearch("");
  }

  const menu = (
    <ul
      ref={menuRef}
      style={{
        position: "fixed",
        left: menuPos?.left,
        width: menuPos?.width,
        top: menuPos?.top,
        bottom: menuPos?.bottom,
      }}
      className="z-50 max-h-48 overflow-y-auto rounded-lg border border-ink/15 bg-white py-1 shadow-lg"
    >
      {filtered.map((tag) => {
        const isSelected = values.includes(tag._id);
        return (
          <li key={tag._id}>
            <button
              type="button"
              onClick={() => toggle(tag._id)}
              className={`flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-sm transition-colors hover:bg-ink/5 ${
                isSelected ? "font-semibold text-ink" : "text-ink/70"
              }`}
            >
              <span>{tag[nameField]}</span>
              {isSelected && <span aria-hidden="true">✓</span>}
            </button>
          </li>
        );
      })}
      {filtered.length === 0 && !canAdd && (
        <li className="px-3 py-1.5 text-sm italic text-ink/40">No matches</li>
      )}
      {canAdd && (
        <li>
          <button
            type="button"
            onClick={handleAdd}
            className="w-full px-3 py-1.5 text-left text-sm text-seal transition-colors hover:bg-ink/5"
          >
            + Add &ldquo;{search.trim()}&rdquo;
          </button>
        </li>
      )}
    </ul>
  );

  return (
    <div ref={containerRef}>
      {/* Chips render inside the field, ahead of the input, so the control
          reads as one combobox; clicking anywhere in it focuses the input */}
      <div
        ref={fieldRef}
        onClick={() => {
          inputRef.current?.focus();
          setOpen(true);
        }}
        className="flex w-full cursor-text flex-wrap items-center gap-1.5 border-b border-ink/25 py-2 transition-colors focus-within:border-ink"
      >
        {selected.map((tag) => (
          <span
            key={tag._id}
            className="inline-flex items-center gap-1.5 rounded-full bg-ink px-2.5 py-0.5 text-xs text-paper"
          >
            {tag[nameField]}
            <button
              type="button"
              aria-label={`Remove ${tag[nameField]}`}
              onClick={(evt) => {
                // Removing a chip shouldn't focus the input or open the menu
                evt.stopPropagation();
                toggle(tag._id);
              }}
              className="leading-none transition-colors hover:text-paper/60"
            >
              ×
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          placeholder={
            selected.length > 0 ? "" : `Search or add ${label.toLowerCase()}`
          }
          value={search}
          onFocus={() => setOpen(true)}
          onChange={(evt) => {
            setSearch(evt.target.value);
            setOpen(true);
          }}
          onKeyDown={(evt) => {
            // The form page has no wrapping <form>; use Enter to add the
            // typed value (or select its exact match) instead
            if (evt.key === "Enter") {
              evt.preventDefault();
              if (canAdd) handleAdd();
              else if (exactMatch) {
                toggle(exactMatch._id);
                setSearch("");
              }
            }
            // Backspace in an empty input removes the last chip
            if (evt.key === "Backspace" && search === "" && values.length > 0) {
              onChange(values.slice(0, -1));
            }
            if (evt.key === "Escape") setOpen(false);
          }}
          className="min-w-[10rem] flex-1 bg-transparent text-sm text-ink outline-none placeholder:text-ink/40"
        />
      </div>

      {open && menuPos && createPortal(menu, document.body)}
    </div>
  );
}
